import { ApplicationInitStatus, Injectable, Injector } from '@angular/core';
import { GlobalConfig, ToastrService } from 'ngx-toastr';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from 'src/app/shared/models/item.model';
import { IdentityService } from '../services/identity.service';

/** Class added to every toast in Unified Code mode (styled in styles/unified-code/components/_components.scss). */
export const UNIFIED_TOAST_CLASS = 'ngx-toastr uc-toast';

/**
 * Toast options for Unified Code mode (DGA criterion 18: temporary notifications are toasts,
 * closable, placed at the reading-start top corner). Pure so it can be unit tested.
 */
export function unifiedToastrConfig(isRtl: boolean): Partial<GlobalConfig> {
  return {
    positionClass: isRtl ? 'toast-top-left' : 'toast-top-right',
    closeButton: true,
    progressBar: false,
    timeOut: 6000,
    toastClass: UNIFIED_TOAST_CLASS,
    tapToDismiss: false,
  };
}

/**
 * Applies the Unified Code toast options to the global ngx-toastr configuration.
 * Custom mode leaves ToastrModule.forRoot() defaults untouched.
 */
@Injectable({
  providedIn: 'root',
})
export class UnifiedToastrService {
  private listening = false;

  constructor(private toastr: ToastrService, private identityService: IdentityService, private injector: Injector) { }

  apply(): void {
    if (!this.identityService.isUnifiedCode()) return;

    Object.assign(this.toastr.toastrConfig, unifiedToastrConfig(this.isRtl()));
    this.followLanguage();
  }

  private isRtl(): boolean {
    const dir = document.documentElement.dir;
    if (dir) return dir === 'rtl';
    return (localStorage.getItem('exab-lang') ?? 'ar') === 'ar';
  }

  /** The language can be toggled without a reload; toasts follow the new reading direction. */
  private followLanguage(): void {
    if (this.listening) return;
    this.listening = true;
    this.injector.get(LanguageService).languageChange.subscribe((language: LanguageEnum) => {
      this.toastr.toastrConfig.positionClass = unifiedToastrConfig(language === LanguageEnum.ar).positionClass;
    });
  }
}

/**
 * APP_INITIALIZER factory. Initializers start together, so the identity (and therefore the design mode)
 * is not known yet when this runs; the options are applied once every initializer — including
 * setIdentity — has resolved, which is still before the root component is bootstrapped.
 */
export function initUnifiedToastr(service: UnifiedToastrService, injector: Injector) {
  return (): void => {
    injector.get(ApplicationInitStatus).donePromise.then(() => service.apply());
  };
}
